// @react
import React, { ReactElement, useState, useContext } from 'react'
// @libraries
import { makeStyles, Theme } from '@material-ui/core/styles'
// @graphql
import { useMutation, useQuery } from '@apollo/client'
// @types
import {
  GetUserQuery,
  GetUserQueryVariables,
  Topic,
  UserFollowTopicMutation,
  UserFollowTopicMutationVariables,
  UserUnfollowTopicMutation,
} from 'lib/graphql/graphqlTypes'
// @common
import { SnackbarContext } from 'modules/common/context/snackbar'
// @components
import Grid from '@bit/atd.web.grid'
import FollowCard from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Follow'
import QueryError from 'modules/common/components/Errors/QueryError'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
// @graphql
import { GET_USER } from 'graphql/queries/user.query'
import {
  USER_FOLLOW_TOPIC,
  USER_UNFOLLOW_TOPIC,
} from 'graphql/mutations/topic.mutation'
import client from 'graphql/Apollo/apolloClient'

/**
 * 5.0.7 Profile - Topics - FollowedTopics + Recommended Topics
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3233%3A6661
 *
 * endpoint: user/profile
 * response: UserProfileDTO
 *
 * fields:
 * topics
 */

const useStyles = makeStyles((theme: Theme) => ({
  cards: {
    marginTop: '16px',
    '& .MuiGrid-item': {
      [theme.breakpoints.down('sm')]: {
        '@media (min-width: 375px)': {
          flexGrow: 0,
          maxWidth: '50%',
          flexBasis: '50%',
        },
        '@media (min-width: 670px)': {
          flexGrow: 0,
          maxWidth: '33.333%',
          flexBasis: '33.333%',
        },
      },
    },
  },
}))

const TopicsList = (): ReactElement => {
  /******************************************
   * STATE
   ******************************************/
  const [isSubmitting, setIsMutationSubmitting] = useState<string | null>(null)
  /******************************************
   * HOOKS
   ******************************************/
  const snackbar = useContext(SnackbarContext)
  const classes = useStyles()

  /******************************************
   * GQL HOOKS
   ******************************************/
  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, { fetchPolicy: 'cache-first' })

  const [followTopic] = useMutation<
    UserFollowTopicMutation,
    UserFollowTopicMutationVariables
  >(USER_FOLLOW_TOPIC)

  const [unfollowTopic] = useMutation<
    UserUnfollowTopicMutation,
    UserFollowTopicMutationVariables
  >(USER_UNFOLLOW_TOPIC)

  /******************************************
   * FUNCTIONS
   ******************************************/

  /**
   *
   * @param item
   */
  const unfollowTopicAction = async (item: Topic) => {
    setIsMutationSubmitting(item.id)
    const { errors: mutationErrors } = await unfollowTopic({
      variables: {
        input: {
          topicKey: item.key,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: 'Successfully unfollowed topic',
        show: true,
      })

      client.cache.modify({
        id: client.cache.identify(item.userStatus),
        fields: {
          followed() {
            return false
          },
        },
      })
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: 'There was a problem unfollowing this topic',
        show: true,
      })
    }
    setIsMutationSubmitting(null)
  }

  /**
   *
   * @param item
   */
  const followTopicAction = async (item: Topic) => {
    setIsMutationSubmitting(item.id)
    const { errors: mutationErrors } = await followTopic({
      variables: {
        input: {
          topicKey: item.key,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: 'Successfully followed topic',
        show: true,
      })

      client.cache.modify({
        id: client.cache.identify(item.userStatus),
        fields: {
          followed() {
            return true
          },
        },
      })
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: 'There was a problem following this topic',
        show: true,
      })
    }
    setIsMutationSubmitting(null)
  }

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  /**
   *
   */
  const skelContent = Array(28)
    .fill('')
    .map((item, index) => {
      return (
        <Grid key={`ske_loader_${index}`} xs={12} sm={3} item>
          <SkeletonLoader />
        </Grid>
      )
    })

  /**
   *
   */
  const topicsCards = data?.user?.topics.map((item) => {
    return (
      <Grid key={`${item.__typename}_${item.id}`} xs={12} sm={3} item>
        <ConditionalLoader condition={isSubmitting !== item.id}>
          <FollowCard
            title={item.label}
            titleURL={item.url}
            following={item.userStatus?.followed}
            btnLabel={item.userStatus?.followed ? 'Unfollow' : 'Follow'}
            onButtonClick={
              item.userStatus?.followed
                ? () => unfollowTopicAction(item)
                : () => followTopicAction(item)
            }
          />
        </ConditionalLoader>
      </Grid>
    )
  })

  return (
    <Grid className={classes.cards} container spacing={3}>
      {isLoading ? skelContent : topicsCards}
    </Grid>
  )
}

export default TopicsList
